import log from 'loglevel'
// WARNING: any imports should not use the logger
import { datadogLogs } from '@datadog/browser-logs'
import { datadogRum } from '@datadog/browser-rum'
import { CONSENT_POLICY_VERSION } from '../analytics/analytics.constants'
import { AppConfig, EnvConfig } from '../config/config'
import { isEuProd } from './environment'

// Note: the order of initialization events may matter so be careful when adding
// new code here or rearranging the existing code.

const consentStorageKey = `${EnvConfig.name}_trustero_consent`

function hasCachedConsent(): boolean {
  const raw = sessionStorage.getItem(consentStorageKey)
  if (!raw) return false
  try {
    const parsed = JSON.parse(raw)
    return (
      parsed.policyVersion === CONSENT_POLICY_VERSION &&
      parsed.status === 'accepted'
    )
  } catch {
    return false
  }
}

let datadogInitialized = false

// Configure Datadog error + performance monitoring.
// https://docs.datadoghq.com/logs/log_collection/javascript/
// https://docs.datadoghq.com/real_user_monitoring/browser/
export const initDatadogMonitoring = (): void => {
  if (datadogInitialized) {
    return
  }
  datadogInitialized = true

  const datadogConfig = EnvConfig.monitoring?.datadog
  // Defaults to the US1 site; envs hosted on a different Datadog org (e.g. EU) set
  // monitoring.datadog.site in config.json.
  const datadogSite = datadogConfig?.site || 'datadoghq.com'
  datadogLogs.init({
    clientToken: datadogConfig?.clientToken || '',
    env: EnvConfig.name,
    forwardErrorsToLogs: datadogConfig?.forwardErrorsToLogs || false,
    sampleRate: datadogConfig?.sampleRate || 0,
    service: 'app',
    site: datadogSite,
    silentMultipleInit: true,
    version: process.env.REACT_APP_VERSION,
  })

  datadogRum.init({
    applicationId:
      datadogConfig?.rumApplicationId ||
      AppConfig.monitoring.datadogRumApplicationId,
    clientToken:
      datadogConfig?.rumClientToken ||
      AppConfig.monitoring.datadogRumClientToken,
    service: 'app',
    site: datadogSite,
    env: EnvConfig.name,
    version: process.env.REACT_APP_VERSION,
    sessionSampleRate: datadogConfig?.sampleRate,
    sessionReplaySampleRate: datadogConfig?.sampleRate,
    silentMultipleInit: true,
    trackResources: true,
    trackLongTasks: true,
    trackUserInteractions: true,
  })
}

// Outside the EU, keep today's behavior exactly: init immediately at import
// time. In the EU, only init immediately if a prior session already
// recorded acceptance (sessionStorage survives within the tab/session);
// otherwise ConsentContext.accept() triggers initDatadogMonitoring() the
// moment consent is granted.
if (!isEuProd() || hasCachedConsent()) {
  initDatadogMonitoring()
}

// -----------------------------------------------------------------------------
// Configure the logging library _after_ the monitoring library initialization.

// put "loglevel" into the window for easy access
declare global {
  interface Window {
    loglevel: log.RootLogger
  }
}
window.loglevel = log

// the following logger initialization needs to happen before other files are
// imported that depend on `log.getContext()`.
log.setDefaultLevel(EnvConfig.debugLevel)

const logger = log.getLogger('app')

logger.debug('Deployment Environment', process.env.REACT_APP_DEPLOYMENT_ENV)
logger.debug('Environment ', EnvConfig.name)
logger.debug(
  'git-hash: ',
  process.env.REACT_APP_VERSION ?? 'env var GIT_HASH was not set',
)
logger.debug(
  'git-branch: ',
  process.env.REACT_APP_BRANCH ?? 'env var GIT_BRANCH was not set',
)
logger.debug(
  'git-user: ',
  process.env.REACT_APP_USER ?? 'env var GIT_USER was not set',
)
logger.debug('ntrcedUrl', EnvConfig.ntrceApiUrl)

export const AppInit = (): void => {
  // initialization work goes here
  logger.debug('init: global initialization is complete')
}
